import React, { useState, useCallback, useEffect, useMemo } from 'react';
import BasicInfoSection from './sections/BasicInfoSection';
import ContactInfoSection from './sections/ContactInfoSection';
import ParticipationModalitySection from './sections/ParticipationModalitySection';
import ExpoZoneSection from './sections/ExpoZoneSection';
import ThematicAxesSection from './sections/ThematicAxesSection';
import SearchableSelect from './SearchableSelect';
import CustomSelect from './CustomSelect';
import {
  BasicInstitutionalInfo,
  ContactInfo,
  InstitutionalDescription,
  ParticipationModality,
} from '../types/profile.types';
import { participationModalities as defaultParticipationModalities } from '../data/profileData';
import { FileTextIcon, MapPinIcon, MicIcon, CalendarIcon, BookIcon, UsersIcon, CheckCircleIcon } from './icons/AdminIcons';
import './CreateContactModal.css';

/** Validación de formato de correo (igual que en la sección de contacto de iniciativas del perfil privado) */
function validateContactEmail(email: string): boolean {
  if (!email || email.trim() === '') return true; // Vacío se valida con obligatorio
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
}

/** Etapas por defecto cuando el pipeline no tiene etapas cargadas (mismo que en tarjetas Empresas) */
const DEFAULT_NEGOCIO_STAGES: { id: string; label: string; color: string; position: number }[] = [
  { id: 'nuevo', label: 'Nuevo', color: '#94a3b8', position: 0 },
  { id: 'contactado', label: 'Contactado', color: '#3b82f6', position: 1 },
  { id: 'calificado', label: 'Calificado', color: '#8b5cf6', position: 2 },
  { id: 'en_proceso', label: 'En proceso', color: '#f59e0b', position: 3 },
  { id: 'negociacion', label: 'Negociación', color: '#ec4899', position: 4 },
  { id: 'completado', label: 'Completado', color: '#22c55e', position: 5 },
  { id: 'perdido', label: 'Perdido', color: '#ef4444', position: 6 },
];

const emptyBasicInfo: BasicInstitutionalInfo = {
  organizationName: '',
  organizationType: '',
  country: '',
  website: '',
  industries: [],
  eventProgramParticipation: [],
  socialNetworks: {},
  thematicAxes: [],
};

const emptyContactInfo: ContactInfo = {
  firstName: '',
  lastName: '',
  position: '',
  email: '',
  phone: '',
  codigoPais: 'AR',
  numeroTelefono: '',
  area: '',
  subarea: '',
  linkedin: '',
  wantsToManageProfile: false,
  wantsToParticipateBusinessRounds: false,
  thematicAxes: [],
};

const emptyInstitutionalDescription: InstitutionalDescription = {
  description: '',
};

/** Organización mínima para el desplegable (solo front, sin backend aún) */
export interface CreateContactOrganizationOption {
  id: number;
  organizacion?: string | null;
  dominio?: string | null;
}

/** Pipeline para desplegable y field_schema para cuerpo del negocio */
export interface CreateContactPipelineOption {
  id: number;
  name: string;
  field_schema?: { key: string; label: string }[];
}

/** Negocio = organización en un pipeline (organization_pipeline_data) */
export interface CreateContactNegocioOption {
  organizationId: number;
  pipelineId: number;
  pipelineName: string;
  pipelineStage: string;
  priority: string;
  data?: Record<string, unknown>;
}

export interface CreateContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (userIdOrContactId: number) => void;
  getApiUrl: () => string;
  isDarkMode?: boolean;
  asSidebar?: boolean;
  onCrmContactCreated?: () => void;
  organizationsList?: CreateContactOrganizationOption[];
  pipelinesList?: CreateContactPipelineOption[];
  /** Negocios por organización (para mostrar los de la org seleccionada en el pipeline elegido) */
  organizationNegociosList?: CreateContactNegocioOption[];
  /** Etapas por pipeline para mostrar igual que en tarjetas de contactos/organizaciones */
  stagesByPipelineId?: Record<number, { id: string; label: string; color: string; position: number }[]>;
  /** Opciones de modalidad (para etiquetas en cuerpo del negocio Scesde26) */
  participationModalityOptions?: { value: string; label: string }[];
}

const CreateContactModal: React.FC<CreateContactModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  getApiUrl,
  isDarkMode = false,
  asSidebar = false,
  onCrmContactCreated,
  organizationsList = [],
  pipelinesList = [],
  organizationNegociosList = [],
  stagesByPipelineId = {},
  participationModalityOptions = defaultParticipationModalities,
}) => {
  const [basicInfo, setBasicInfo] = useState<BasicInstitutionalInfo>(emptyBasicInfo);
  const [contactInfo, setContactInfo] = useState<ContactInfo>(emptyContactInfo);
  const [institutionalDescription, setInstitutionalDescription] = useState<InstitutionalDescription>(emptyInstitutionalDescription);
  const [participationModalities, setParticipationModalities] = useState<ParticipationModality[]>([]);
  const [agreedModality, setAgreedModality] = useState('');
  const [modalityObservations, setModalityObservations] = useState('');
  const [eventProgramParticipation, setEventProgramParticipation] = useState<string[]>([]);
  const [requestInformation, setRequestInformation] = useState(false);
  const [requestExpoInformation, setRequestExpoInformation] = useState(false);
  const [selectedStands, setSelectedStands] = useState<string[]>([]);
  const [confirmedStands, setConfirmedStands] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contactInfoErrors, setContactInfoErrors] = useState<Record<string, string>>({});

  // Asociar contacto con organización
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(null);
  const [showNewOrganizationFields, setShowNewOrganizationFields] = useState(false);
  // Asociar contacto a negocio: se elige pipeline y se muestran los negocios de la org en ese pipeline
  const [selectedPipelineId, setSelectedPipelineId] = useState<number | null>(null);
  /** Checkbox(es): asociar el nuevo contacto a cada negocio mostrado (por ahora un solo negocio por org por pipeline) */
  const [associateContactToNegocioIds, setAssociateContactToNegocioIds] = useState<Set<string>>(new Set());
  const [showCreateDealFields, setShowCreateDealFields] = useState(false);
  /** Etapa y prioridad del negocio a crear (cuando "Crear negocio") */
  const [newDealStageId, setNewDealStageId] = useState('');
  const [newDealPriority, setNewDealPriority] = useState<'high' | 'medium' | 'low'>('medium');
  /** Valores de los campos del pipeline (cuando "Crear negocio" es para un pipeline distinto de Scesde26) */
  const [otherPipelineDealData, setOtherPipelineDealData] = useState<Record<string, string>>({});
  /** Crear perfil de usuario para el contacto (solo en CRM): usuario con correo del contacto y contraseña generada */
  const [createProfileForContact, setCreateProfileForContact] = useState(false);
  /** Tras crear contacto con perfil: mostrar contraseña generada para copiar */
  const [createdPasswordResult, setCreatedPasswordResult] = useState<{ email: string; password: string } | null>(null);

  const orgOptionLabel = useCallback((org: CreateContactOrganizationOption) => {
    const suffix = (org.dominio && String(org.dominio).trim()) || String(org.id);
    return org.organizacion ? `${org.organizacion} (${suffix})` : `Organización ${suffix}`;
  }, []);
  const organizationSelectOptions = useMemo(() => organizationsList.map(orgOptionLabel), [organizationsList, orgOptionLabel]);
  const selectedOrgForSelect = selectedOrganizationId != null ? organizationsList.find((o) => o.id === selectedOrganizationId) : null;
  const organizationSelectValue = selectedOrgForSelect ? orgOptionLabel(selectedOrgForSelect) : '';

  const pipelineSelectOptions = useMemo(() => pipelinesList.map((p) => p.name), [pipelinesList]);
  const selectedPipelineForSelect = selectedPipelineId != null ? pipelinesList.find((p) => p.id === selectedPipelineId) : null;
  const pipelineSelectValue = selectedPipelineForSelect ? selectedPipelineForSelect.name : '';

  /** Negocios de la organización seleccionada en el pipeline seleccionado (0 o 1 por pipeline) */
  const negociosDeOrgEnPipeline = useMemo(() => {
    if (selectedOrganizationId == null || selectedPipelineId == null) return [];
    return organizationNegociosList.filter(
      (n) => n.organizationId === selectedOrganizationId && n.pipelineId === selectedPipelineId
    );
  }, [organizationNegociosList, selectedOrganizationId, selectedPipelineId]);

  useEffect(() => {
    if (showCreateDealFields && selectedPipelineId != null && newDealStageId === '') {
      const stages = (stagesByPipelineId[selectedPipelineId] || DEFAULT_NEGOCIO_STAGES).slice().sort((a, b) => a.position - b.position);
      if (stages.length > 0) setNewDealStageId(stages[0].id);
    }
  }, [showCreateDealFields, selectedPipelineId, stagesByPipelineId, newDealStageId]);

  const toggleAssociateToNegocio = useCallback((key: string) => {
    setAssociateContactToNegocioIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const parseArr = useCallback((val: unknown): string[] => {
    if (val == null) return [];
    if (Array.isArray(val)) return val.map((v) => String(v));
    if (typeof val === 'string') {
      try {
        const p = JSON.parse(val);
        return Array.isArray(p) ? p.map((x: unknown) => String(x)) : [val];
      } catch {
        return [val];
      }
    }
    return [];
  }, []);

  const parseParticipation = useCallback((val: unknown): unknown[] => {
    if (val == null) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
      try {
        const p = JSON.parse(val);
        return Array.isArray(p) ? p : [];
      } catch {
        return [];
      }
    }
    return [];
  }, []);

  const getSpeakerStatusHelper = useCallback((speaker: Record<string, unknown> | null): { completionStatus: 'complete' | 'incomplete'; hasRequestApproval: boolean } => {
    if (!speaker) return { completionStatus: 'incomplete', hasRequestApproval: false };
    const hasPhoto = speaker.photo && (typeof speaker.photo === 'string' ? (speaker.photo as string).trim() !== '' : true);
    const hasFirstName = speaker.firstName && String(speaker.firstName).trim() !== '';
    const hasLastName = speaker.lastName && String(speaker.lastName).trim() !== '';
    const hasNationality = speaker.nationality && String(speaker.nationality).trim() !== '';
    const hasOrganization = speaker.organization && String(speaker.organization).trim() !== '';
    const hasPosition = speaker.position && String(speaker.position).trim() !== '';
    const hasDescription = speaker.description && String(speaker.description).trim() !== '';
    const hasThematicAxes = speaker.thematicAxes && Array.isArray(speaker.thematicAxes) && speaker.thematicAxes.length > 0;
    const hasContentDescription = speaker.contentDescription && String(speaker.contentDescription).trim() !== '';
    const hasLinkedin = speaker.linkedin && String(speaker.linkedin).trim() !== '';
    const hasBiography = speaker.biography && String(speaker.biography).trim() !== '';
    const hasValidBiography = hasLinkedin || hasBiography;
    const isComplete = Boolean(hasPhoto && hasFirstName && hasLastName && hasNationality && hasOrganization &&
      hasPosition && hasDescription && hasThematicAxes && hasContentDescription && hasValidBiography);
    return { completionStatus: isComplete ? 'complete' : 'incomplete', hasRequestApproval: speaker.requestApproval === true };
  }, []);

  const getEventStatusHelper = useCallback((event: Record<string, unknown> | null): { completionStatus: 'complete' | 'incomplete'; hasRequestApproval: boolean } => {
    if (!event) return { completionStatus: 'incomplete', hasRequestApproval: false };
    const hasName = event.name && String(event.name).trim() !== '';
    const hasDescription = event.description && String(event.description).trim() !== '';
    const hasSpeakers = (event.speakerIds && Array.isArray(event.speakerIds) && event.speakerIds.length > 0) ||
      (event.localSpeakers && Array.isArray(event.localSpeakers) && event.localSpeakers.length > 0);
    const hasDuration = event.duration && String(event.duration).trim() !== '';
    const hasThematicAxes = event.thematicAxes && Array.isArray(event.thematicAxes) && event.thematicAxes.length > 0;
    const hasAttendeeProfiles = event.attendeeProfiles && Array.isArray(event.attendeeProfiles) && event.attendeeProfiles.length > 0;
    const isComplete = Boolean(hasName && hasDescription && hasSpeakers && hasDuration && hasThematicAxes && hasAttendeeProfiles);
    return { completionStatus: isComplete ? 'complete' : 'incomplete', hasRequestApproval: event.requestApproval === true };
  }, []);

  const getInitiativeStatusHelper = useCallback((initiative: Record<string, unknown> | null): { completionStatus: 'complete' | 'incomplete'; hasRequestApproval: boolean } => {
    if (!initiative) return { completionStatus: 'incomplete', hasRequestApproval: false };
    const hasName = initiative.name && String(initiative.name).trim() !== '';
    const hasShortDescription = initiative.shortDescription && String(initiative.shortDescription).trim() !== '';
    const hasDescription = initiative.description && String(initiative.description).trim() !== '';
    const hasThematicAxes = initiative.thematicAxes && Array.isArray(initiative.thematicAxes) && initiative.thematicAxes.length > 0;
    let isComplete = Boolean(hasName && hasShortDescription && hasDescription && hasThematicAxes);
    if (!initiative.noContact) {
      const hasContactFirstName = initiative.contactFirstName != null && String(initiative.contactFirstName).trim() !== '';
      const hasContactLastName = initiative.contactLastName != null && String(initiative.contactLastName).trim() !== '';
      const hasContactPosition = initiative.contactPosition != null && String(initiative.contactPosition).trim() !== '';
      const hasContactEmail = initiative.contactEmail != null && String(initiative.contactEmail).trim() !== '';
      isComplete = Boolean(isComplete && hasContactFirstName && hasContactLastName && hasContactPosition && hasContactEmail);
    }
    return { completionStatus: isComplete ? 'complete' : 'incomplete', hasRequestApproval: initiative.requestApproval === true };
  }, []);

  const getBusinessRoundsStatusHelper = useCallback((participant: Record<string, unknown> | null): 'complete' | 'incomplete' => {
    if (!participant) return 'incomplete';
    const hasFirstName = participant.firstName && String(participant.firstName).trim() !== '';
    const hasLastName = participant.lastName && String(participant.lastName).trim() !== '';
    const hasPosition = participant.position && String(participant.position).trim() !== '';
    const hasOrganization = participant.organization && String(participant.organization).trim() !== '';
    const hasEmail = participant.email && String(participant.email).trim() !== '';
    const hasPhone = (participant.phone && String(participant.phone).trim() !== '') ||
      (participant.numeroTelefono && String(participant.numeroTelefono).trim() !== '');
    const hasThematicAxes = participant.thematicAxes && Array.isArray(participant.thematicAxes) && participant.thematicAxes.length > 0;
    return (hasFirstName && hasLastName && hasPosition && hasOrganization && hasEmail && hasPhone && hasThematicAxes) ? 'complete' : 'incomplete';
  }, []);

  const resetForm = useCallback(() => {
    setBasicInfo(emptyBasicInfo);
    setContactInfo(emptyContactInfo);
    setInstitutionalDescription(emptyInstitutionalDescription);
    setParticipationModalities([]);
    setAgreedModality('');
    setModalityObservations('');
    setEventProgramParticipation([]);
    setRequestInformation(false);
    setRequestExpoInformation(false);
    setSelectedStands([]);
    setConfirmedStands([]);
    setError(null);
    setContactInfoErrors({});
    setSelectedOrganizationId(null);
    setShowNewOrganizationFields(false);
    setSelectedPipelineId(null);
    setAssociateContactToNegocioIds(new Set());
    setShowCreateDealFields(false);
    setOtherPipelineDealData({});
    setCreateProfileForContact(false);
    setCreatedPasswordResult(null);
  }, []);

  // Validar formato de correo al escribir (igual que en iniciativas del perfil privado)
  useEffect(() => {
    const email = (contactInfo.email || '').trim().toLowerCase();
    if (!email) {
      setContactInfoErrors(prev => {
        const next = { ...prev };
        delete next.email;
        return next;
      });
    } else if (!validateContactEmail(email)) {
      setContactInfoErrors(prev => ({ ...prev, email: 'Por favor, ingresa un correo electrónico válido.' }));
    } else {
      setContactInfoErrors(prev => {
        const next = { ...prev };
        delete next.email;
        return next;
      });
    }
  }, [contactInfo.email]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const buildPayload = (): Record<string, unknown> => {
    const phone = contactInfo.phone || (contactInfo.codigoPais && contactInfo.numeroTelefono
      ? `${contactInfo.codigoPais} ${contactInfo.numeroTelefono}`.trim()
      : '');
    return {
      email: (contactInfo.email || '').trim().toLowerCase(),
      organization_name: basicInfo.organizationName || null,
      first_name: contactInfo.firstName || null,
      last_name: contactInfo.lastName || null,
      phone: phone || null,
      position: contactInfo.position || null,
      area: contactInfo.area || null,
      subarea: contactInfo.subarea || null,
      linkedin: (contactInfo.linkedin || '').trim() || null,
      organization_type: basicInfo.organizationType || null,
      website: basicInfo.website || null,
      industries: basicInfo.industries && basicInfo.industries.length > 0 ? basicInfo.industries : null,
      country: basicInfo.country || null,
      description: institutionalDescription.description || null,
      organization_thematic_axes: basicInfo.thematicAxes && basicInfo.thematicAxes.length > 0 ? basicInfo.thematicAxes : null,
      social_networks: basicInfo.socialNetworks && Object.keys(basicInfo.socialNetworks || {}).length > 0 ? basicInfo.socialNetworks : null,
      logo: (basicInfo.logo && typeof basicInfo.logo === 'string') ? basicInfo.logo : null,
      personal_thematic_axes: contactInfo.thematicAxes && contactInfo.thematicAxes.length > 0 ? contactInfo.thematicAxes : null,
      wants_to_manage_profile: contactInfo.wantsToManageProfile ?? false,
      wants_to_participate_business_rounds: contactInfo.wantsToParticipateBusinessRounds ?? false,
      event_program_participation: eventProgramParticipation.length > 0 ? eventProgramParticipation : null,
      participation_modalities: participationModalities.length > 0 ? participationModalities : null,
      agreed_modality: agreedModality || null,
      modality_observations: modalityObservations || null,
      request_information: requestInformation,
      request_expo_information: requestExpoInformation,
      selected_stands_updated: selectedStands.length > 0 ? selectedStands : null,
      confirmed_stands: confirmedStands.length > 0 ? confirmedStands : null,
    };
  };

  const handleSubmit = async () => {
    const email = (contactInfo.email || '').trim().toLowerCase();
    if (!email) {
      setContactInfoErrors({ email: 'El correo electrónico es obligatorio.' });
      setError(null);
      setTimeout(() => {
        document.getElementById('create-contact-email')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return;
    }
    if (!validateContactEmail(email)) {
      setContactInfoErrors({ email: 'Por favor, ingresa un correo electrónico válido.' });
      setError(null);
      setTimeout(() => {
        document.getElementById('create-contact-email')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return;
    }
    setContactInfoErrors({});
    setError(null);
    setSubmitting(true);
    try {
      const baseUrl = getApiUrl();

      if (asSidebar) {
        const phone = contactInfo.phone || (contactInfo.codigoPais && contactInfo.numeroTelefono
          ? `${contactInfo.codigoPais} ${contactInfo.numeroTelefono}`.trim()
          : '');
        const assignToNegocios = negociosDeOrgEnPipeline
          .filter((n) => associateContactToNegocioIds.has(`${n.organizationId}-${n.pipelineId}`))
          .map((n) => ({ organizationId: n.organizationId, pipelineId: n.pipelineId }));

        const isScesde26Pipeline = selectedPipelineId === 1 || pipelinesList.find((p) => p.id === selectedPipelineId)?.name === 'Scesde26';

        const body: Record<string, unknown> = {
          correo: email,
          nombre: (contactInfo.firstName || '').trim() || null,
          apellido: (contactInfo.lastName || '').trim() || null,
          cargo: (contactInfo.position || '').trim() || null,
          area: (contactInfo.area || '').trim() || null,
          subarea: (contactInfo.subarea || '').trim() || null,
          linkedin: (contactInfo.linkedin || '').trim() || null,
          telefono: phone || null,
          organization_ids: showNewOrganizationFields ? [] : (selectedOrganizationId != null ? [selectedOrganizationId] : []),
          ...(assignToNegocios.length > 0 ? { assign_to_negocios: assignToNegocios } : {}),
          create_profile: createProfileForContact,
          created_organization_in_this_flow: showNewOrganizationFields,
          created_scesde26_negocio_in_this_flow: showCreateDealFields && isScesde26Pipeline,
        };

        if (showNewOrganizationFields) {
          body.create_organization = {
            organizacion: (basicInfo.organizationName || '').trim() || null,
            tipo_organizacion: basicInfo.organizationType || null,
            industrias: basicInfo.industries && basicInfo.industries.length > 0 ? basicInfo.industries : null,
            sitio_web: (basicInfo.website || '').trim() || null,
            pais_origen: basicInfo.country || null,
            descripcion: (institutionalDescription.description || '').trim() || null,
            ejes_tematicos: basicInfo.thematicAxes && basicInfo.thematicAxes.length > 0 ? basicInfo.thematicAxes : null,
            redes_sociales: basicInfo.socialNetworks && Object.keys(basicInfo.socialNetworks || {}).length > 0 ? basicInfo.socialNetworks : null,
            logo: (basicInfo.logo && typeof basicInfo.logo === 'string') ? basicInfo.logo : null,
          };
        }

        if (showCreateDealFields && selectedPipelineId != null) {
          const orgIdForNegocio = showNewOrganizationFields ? null : selectedOrganizationId;
          const dealData = isScesde26Pipeline
            ? {
                participation_modalities: participationModalities.length > 0 ? participationModalities : null,
                agreed_modality: agreedModality || null,
                modality_observations: (modalityObservations || '').trim() || null,
                event_program_participation: eventProgramParticipation.length > 0 ? eventProgramParticipation : null,
                request_information: requestInformation,
                request_expo_information: requestExpoInformation,
                selected_stands_updated: selectedStands.length > 0 ? selectedStands : null,
                confirmed_stands: confirmedStands.length > 0 ? confirmedStands : null,
              }
            : otherPipelineDealData;
          body.create_negocio = {
            organization_id: orgIdForNegocio,
            pipeline_id: selectedPipelineId,
            pipeline_stage: newDealStageId || 'nuevo',
            priority: newDealPriority,
            data: dealData,
            is_scesde26_pipeline: isScesde26Pipeline,
          };
        }

        if (createProfileForContact) {
          body.profile_data = buildPayload();
        }

        const res = await fetch(`${baseUrl}/admin/crm/contacts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const errMsg = data.error || `Error ${res.status}`;
          if (res.status === 409 && (errMsg.includes('correo') || errMsg.includes('Ya existe'))) {
            setContactInfoErrors({ email: errMsg });
            setError(null);
            setTimeout(() => {
              document.getElementById('create-contact-email')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 100);
          } else {
            setError(errMsg);
            setContactInfoErrors({});
          }
          return;
        }
        if (data.success && data.data) {
          const contactId = data.data.contactId;
          onSuccess(contactId);
          onCrmContactCreated?.();
          if (createProfileForContact && data.data.generatedPassword) {
            setCreatedPasswordResult({ email, password: data.data.generatedPassword });
          } else {
            handleClose();
          }
        } else {
          setError(data.error || 'Error al crear el contacto');
        }
        return;
      }

      const payload = buildPayload();
      const res = await fetch(`${baseUrl}/admin/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const errMsg = data.error || `Error ${res.status}`;
        if (res.status === 400 && (errMsg.includes('Ya existe un usuario') || errMsg.toLowerCase().includes('email') || errMsg.toLowerCase().includes('ya existe'))) {
          setContactInfoErrors({ email: errMsg });
          setError(null);
          setTimeout(() => {
            document.getElementById('create-contact-email')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
        } else {
          setError(errMsg);
          setContactInfoErrors({});
        }
        return;
      }
      if (data.success && data.userId) {
        onSuccess(data.userId);
        handleClose();
      } else {
        const errMsg = data.error || 'Error al crear el contacto';
        if (errMsg.includes('Ya existe un usuario') || errMsg.toLowerCase().includes('email') || errMsg.toLowerCase().includes('ya existe')) {
          setContactInfoErrors({ email: errMsg });
          setError(null);
          setTimeout(() => {
            document.getElementById('create-contact-email')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
        } else {
          setError(errMsg);
          setContactInfoErrors({});
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error de conexión');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const handleCopyPassword = () => {
    if (createdPasswordResult?.password) {
      navigator.clipboard.writeText(createdPasswordResult.password);
    }
  };

  if (createdPasswordResult) {
    const successContent = (
      <>
        <div className="create-contact-modal-header">
          <h2>Contacto y perfil creados</h2>
          <button type="button" className="create-contact-modal-close" onClick={handleClose} aria-label="Cerrar">
            ×
          </button>
        </div>
        <div className="create-contact-modal-body">
          <p className="create-contact-hint" style={{ marginBottom: 16 }}>
            Se ha creado el contacto y el usuario con el correo <strong>{createdPasswordResult.email}</strong>. Copia la contraseña y compártela con el usuario (solo se muestra una vez).
          </p>
          <div className="create-contact-section" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label className="create-contact-label">Contraseña generada</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <code style={{ padding: '8px 12px', background: 'var(--admin-bg-secondary, #f1f5f9)', borderRadius: 6, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {createdPasswordResult.password}
              </code>
              <button
                type="button"
                className="create-contact-footer-btn primary"
                onClick={handleCopyPassword}
              >
                Copiar al portapapeles
              </button>
            </div>
          </div>
        </div>
        <div className="create-contact-modal-footer">
          <button type="button" className="create-contact-footer-btn primary" onClick={handleClose}>
            Cerrar
          </button>
        </div>
      </>
    );
    if (asSidebar) {
      return (
        <div className={`create-contact-sidebar-inner ${isDarkMode ? 'dark' : ''}`}>
          {successContent}
        </div>
      );
    }
    return (
      <div className="create-contact-modal-overlay" onClick={handleClose}>
        <div className={`create-contact-modal ${isDarkMode ? 'dark' : ''}`} onClick={(e) => e.stopPropagation()}>
          {successContent}
        </div>
      </div>
    );
  }

  const formContent = (
    <>
      <div className="create-contact-modal-header">
        <h2>Crear nuevo contacto</h2>
        <button type="button" className="create-contact-modal-close" onClick={handleClose} aria-label="Cerrar">
          ×
        </button>
      </div>
      <div className="create-contact-modal-body">
        {error && (
          <div className="create-contact-modal-error">
            {error}
          </div>
        )}

        <section className="create-contact-section">
          <h3>Datos de contacto</h3>
          <ContactInfoSection
            data={contactInfo}
            onChange={setContactInfo}
            errors={contactInfoErrors}
            createContactMode
          />
        </section>

        {/* Asociar contacto con: desplegable organizaciones (mismo componente que resto de selección simple) + botón Crear organización nueva */}
        <section className="create-contact-section">
          <h3>Asociar contacto con</h3>
          <div className="create-contact-associate-org">
            <label className="create-contact-label">Organización</label>
            <div style={{ marginBottom: 12 }}>
              <SearchableSelect
                value={organizationSelectValue}
                onChange={(value) => {
                  const org = organizationsList.find((o) => orgOptionLabel(o) === value);
                  setSelectedOrganizationId(org ? org.id : null);
                  if (org) setShowNewOrganizationFields(false);
                }}
                options={organizationSelectOptions}
                placeholder="Seleccionar organización..."
                allowCustomInput={false}
                allowSearch={true}
              />
            </div>
            <button
              type="button"
              className="create-contact-link-btn"
              onClick={() => {
                setShowNewOrganizationFields(true);
                setSelectedOrganizationId(null);
              }}
            >
              Crear organización nueva
            </button>
          </div>
        </section>

        {/* Datos de la organización: solo si pulsó "Crear organización nueva" */}
        {showNewOrganizationFields && (
          <section className="create-contact-section">
            <h3>Datos de la organización</h3>
            <BasicInfoSection
              data={basicInfo}
              onChange={setBasicInfo}
              institutionalDescription={institutionalDescription}
              onInstitutionalDescriptionChange={setInstitutionalDescription}
              createContactMode
            />
            <ThematicAxesSection
              selectedAxes={basicInfo.thematicAxes || []}
              onChange={(axes) => setBasicInfo(prev => ({ ...prev, thematicAxes: axes }))}
              createContactMode
            />
          </section>
        )}

        {/* Asociar contacto a negocio: misma lógica si eligió una org o si está creando una nueva */}
        <section className="create-contact-section">
          <h3>Asociar contacto a negocio</h3>
          <div className="create-contact-associate-deal">
            <p className="create-contact-hint">
              {showNewOrganizationFields
                ? 'Seleccione el pipeline y asocie el contacto a negocios o cree uno nuevo para la organización que está creando.'
                : 'Primero seleccione o cree una organización arriba. Luego seleccione el pipeline para ver los negocios de esa organización o crear uno nuevo.'}
            </p>
            {(selectedOrganizationId != null || showNewOrganizationFields) && (
              <div className="create-contact-deal-row">
                <label className="create-contact-label">Pipeline</label>
                <div style={{ marginBottom: 12 }}>
                  <SearchableSelect
                    value={pipelineSelectValue}
                    onChange={(value) => {
                      const p = pipelinesList.find((x) => x.name === value);
                      setSelectedPipelineId(p ? p.id : null);
                      setAssociateContactToNegocioIds(new Set());
                    }}
                    options={pipelineSelectOptions}
                    placeholder="Seleccionar pipeline..."
                    allowCustomInput={false}
                    allowSearch={false}
                  />
                </div>
              </div>
            )}
            {(selectedOrganizationId != null || showNewOrganizationFields) && selectedPipelineId != null && (
              <>
                <p className="create-contact-label" style={{ marginBottom: 8 }}>
                  {negociosDeOrgEnPipeline.length > 0
                    ? `La organización tiene un negocio en el pipeline ${pipelineSelectValue}.`
                    : `La organización no tiene negocio en el pipeline ${pipelineSelectValue}.`}
                </p>
                {negociosDeOrgEnPipeline.length > 0 ? (
                  <div className="admin-crm-empresa-negocios-wrap create-contact-negocios-list">
                    <ul className="admin-crm-empresa-negocios-list">
                      {negociosDeOrgEnPipeline.map((n) => {
                        const key = `${n.organizationId}-${n.pipelineId}`;
                        const stageOptions = (stagesByPipelineId[n.pipelineId] || DEFAULT_NEGOCIO_STAGES).map((s) => ({ value: s.id, label: s.label, color: s.color }));
                        const priorityOptions = [
                          { value: 'high', label: 'Alta', color: '#ef4444' },
                          { value: 'medium', label: 'Media', color: '#fbbf24' },
                          { value: 'low', label: 'Baja', color: '#10b981' },
                        ];
                        const d = n.data && typeof n.data === 'object' ? n.data : null;
                        const isScesde26 = n.pipelineId === 1 || n.pipelineName === 'Scesde26';
                        const fieldSchema = pipelinesList.find((p) => p.id === n.pipelineId)?.field_schema ?? [];
                        return (
                          <li key={key} className="admin-crm-empresa-negocio-row">
                            <div className="admin-crm-empresa-negocio-header">
                              <strong>{n.pipelineName}</strong>
                              <div className="admin-crm-empresa-negocio-selects-row" onClick={(e) => e.stopPropagation()}>
                                <label className="admin-crm-empresa-negocio-select-label">
                                  <span className="admin-crm-empresa-negocio-select-label-text">Etapa</span>
                                  <CustomSelect
                                    className="admin-crm-priority-select-small admin-crm-empresa-etapa-select"
                                    optionsClassName={isDarkMode ? 'custom-select-options-dark' : ''}
                                    value={n.pipelineStage}
                                    onChange={() => {}}
                                    options={stageOptions}
                                  />
                                </label>
                                <label className="admin-crm-empresa-negocio-select-label">
                                  <span className="admin-crm-empresa-negocio-select-label-text">Prioridad</span>
                                  <CustomSelect
                                    className="admin-crm-priority-select-small"
                                    optionsClassName={isDarkMode ? 'custom-select-options-dark' : ''}
                                    value={n.priority}
                                    onChange={() => {}}
                                    options={priorityOptions}
                                  />
                                </label>
                              </div>
                            </div>
                            {d && isScesde26 ? (
                              <div className="admin-crm-contact-info-grid admin-crm-empresa-negocio-body">
                                {(() => {
                                  const mods = parseArr(d.participation_modalities);
                                  const agreed = d.agreed_modality != null && d.agreed_modality !== '' ? String(d.agreed_modality) : '';
                                  const modalityLabel = agreed
                                    ? (participationModalityOptions.find((m) => m.value === agreed)?.label ?? agreed)
                                    : mods.length > 0
                                      ? mods.map((mod: string) => participationModalityOptions.find((m) => m.value === mod)?.label ?? mod).join(', ')
                                      : 'N/A';
                                  const stands = parseArr(d.selected_stands_updated);
                                  const standsLabel = stands.length > 0 ? stands.join(', ') : 'N/A';
                                  const speakers = parseParticipation(d.speakers);
                                  const parallelEvents = parseParticipation(d.parallel_events);
                                  const initiatives = parseParticipation(d.iniciativas).length > 0 ? parseParticipation(d.iniciativas) : parseParticipation(d.catalog_sections);
                                  const businessRounds = parseParticipation(d.business_rounds_participants);
                                  const hasAnyParticipation = speakers.length > 0 || parallelEvents.length > 0 || initiatives.length > 0 || businessRounds.length > 0;
                                  return (
                                    <>
                                      <div className="admin-crm-contact-info-item">
                                        <span className="info-icon"><FileTextIcon width={18} height={18} /></span>
                                        <div>
                                          <span className="info-label">Modalidad:</span>
                                          <span className="info-value">{modalityLabel}</span>
                                        </div>
                                      </div>
                                      <div className="admin-crm-contact-info-item">
                                        <span className="info-icon"><MapPinIcon width={18} height={18} /></span>
                                        <div>
                                          <span className="info-label">Zona Expo:</span>
                                          <span className="info-value">{standsLabel}</span>
                                        </div>
                                      </div>
                                      <div className="admin-crm-contact-participations">
                                        <span className="info-label">Participa en:</span>
                                        <div className="admin-crm-participations-badges">
                                          {speakers.length > 0 && <span className="feature-badge-small speakers-badge" title={`${speakers.length} orador(es)`}><MicIcon width={14} height={14} /> {speakers.length}</span>}
                                          {parallelEvents.length > 0 && <span className="feature-badge-small events-badge" title={`${parallelEvents.length} evento(s)`}><CalendarIcon width={14} height={14} /> {parallelEvents.length}</span>}
                                          {initiatives.length > 0 && <span className="feature-badge-small catalog-badge" title="En catálogo"><BookIcon width={14} height={14} /></span>}
                                          {businessRounds.length > 0 && <span className="feature-badge-small rounds-badge" title={`${businessRounds.length} participante(s) en rondas`}><UsersIcon width={14} height={14} /></span>}
                                          {!hasAnyParticipation && <span style={{ color: '#9ca3af', fontSize: '12px' }}>Sin participaciones</span>}
                                        </div>
                                      </div>
                                      {(speakers.length > 0 && speakers.some((s: unknown) => {
                                        const info = getSpeakerStatusHelper(s as Record<string, unknown>);
                                        return (info.completionStatus === 'complete' || info.hasRequestApproval) &&
                                          (s as Record<string, unknown>)?.approval_status !== 'approved' && (s as Record<string, unknown>)?.approval_status !== 'rejected';
                                      })) ||
                                      (parallelEvents.length > 0 && parallelEvents.some((e: unknown) => {
                                        const info = getEventStatusHelper(e as Record<string, unknown>);
                                        return (info.completionStatus === 'complete' || info.hasRequestApproval) &&
                                          (e as Record<string, unknown>)?.approval_status !== 'approved' && (e as Record<string, unknown>)?.approval_status !== 'rejected';
                                      })) ||
                                      (initiatives.length > 0 && initiatives.some((i: unknown) => {
                                        const info = getInitiativeStatusHelper(i as Record<string, unknown>);
                                        return (info.completionStatus === 'complete' || info.hasRequestApproval) &&
                                          (i as Record<string, unknown>)?.approval_status !== 'approved' && (i as Record<string, unknown>)?.approval_status !== 'rejected';
                                      })) ||
                                      (businessRounds.length > 0 && businessRounds.some((p: unknown) =>
                                        getBusinessRoundsStatusHelper(p as Record<string, unknown>) === 'complete' &&
                                        (p as Record<string, unknown>)?.approval_status !== 'approved' && (p as Record<string, unknown>)?.approval_status !== 'rejected'
                                      )) ? (
                                        <div className="admin-crm-contact-pending-actions">
                                          <span className="info-label">Acciones pendientes:</span>
                                          <div className="admin-crm-pending-badges">
                                            <span className="status-badge-small approval-badge"><CheckCircleIcon width={14} height={14} /> Aprobaciones</span>
                                          </div>
                                        </div>
                                      ) : null}
                                    </>
                                  );
                                })()}
                              </div>
                            ) : d && Object.keys(d).filter((k) => k !== 'pipeline_stage' && k !== 'priority').length > 0 ? (
                              <div className="admin-crm-contact-info-grid admin-crm-empresa-negocio-body">
                                {(() => {
                                  const keysToShow = fieldSchema.length > 0
                                    ? fieldSchema.map((f) => f.key)
                                    : Object.keys(d).filter((k) => k !== 'pipeline_stage' && k !== 'priority');
                                  const getLabel = (key: string) => fieldSchema.find((f) => f.key === key)?.label ?? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                                  const formatValue = (val: unknown): React.ReactNode => {
                                    if (val == null) return '—';
                                    if (typeof val === 'boolean') return val ? 'Sí' : 'No';
                                    if (Array.isArray(val)) {
                                      if (val.length === 0) return '—';
                                      const first = val[0];
                                      if (typeof first === 'object' && first !== null) return `${val.length} ítem(s)`;
                                      return val.map((v) => String(v)).join(', ');
                                    }
                                    if (typeof val === 'object') return JSON.stringify(val).length > 80 ? `${JSON.stringify(val).slice(0, 80)}…` : JSON.stringify(val);
                                    return String(val);
                                  };
                                  return keysToShow.map((key) => {
                                    const val = d[key];
                                    if (val === undefined) return null;
                                    return (
                                      <div key={key} className="admin-crm-contact-info-item">
                                        <span className="info-icon"><FileTextIcon width={18} height={18} /></span>
                                        <div>
                                          <span className="info-label">{getLabel(key)}:</span>
                                          <span className="info-value">{formatValue(val)}</span>
                                        </div>
                                      </div>
                                    );
                                  });
                                })()}
                              </div>
                            ) : null}
                            <label className="checkbox-item create-contact-associate-checkbox">
                              <input
                                type="checkbox"
                                checked={associateContactToNegocioIds.has(key)}
                                onChange={() => toggleAssociateToNegocio(key)}
                              />
                              <span className="checkbox-label">Asociar contacto a este negocio</span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : (
                  <div className="create-contact-deal-no-negocio">
                    <button
                      type="button"
                      className="create-contact-link-btn"
                      onClick={() => setShowCreateDealFields(true)}
                    >
                      Crear negocio
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* Campos para "Crear negocio": etapa, prioridad (con círculos) y según pipeline modalidad/stands o campos personalizados */}
        {showCreateDealFields && selectedPipelineId != null && (() => {
          const isScesde26Pipeline = selectedPipelineId === 1 || pipelinesList.find((p) => p.id === selectedPipelineId)?.name === 'Scesde26';
          const pipelineNameDeal = pipelineSelectValue || 'seleccionado';
          const stagesForNewDeal = (stagesByPipelineId[selectedPipelineId] || DEFAULT_NEGOCIO_STAGES).slice().sort((a, b) => a.position - b.position);
          const stageOptionsNewDeal = stagesForNewDeal.map((s) => s.label);
          const selectedStageLabelNewDeal = newDealStageId ? stagesForNewDeal.find((s) => s.id === newDealStageId)?.label ?? '' : (stagesForNewDeal[0]?.label ?? '');
          const priorityOptionsNewDeal = ['Alta', 'Media', 'Baja'];
          const priorityLabelNewDeal = newDealPriority === 'high' ? 'Alta' : newDealPriority === 'low' ? 'Baja' : 'Media';
          const PRIORITY_COLORS: Record<string, string> = { Alta: '#ef4444', Media: '#f59e0b', Baja: '#22c55e' };
          const getPriorityColor = (l: string) => PRIORITY_COLORS[l] ?? '#94a3b8';
          const selectedStageColorNewDeal = newDealStageId ? stagesForNewDeal.find((s) => s.id === newDealStageId)?.color : (stagesForNewDeal[0]?.color ?? '#94a3b8');
          const getStageOptionColor = (label: string) => stagesForNewDeal.find((s) => s.label === label)?.color;

          const etapaPrioridadSection = (
            <section className="create-contact-section">
              <h3>Etapa y prioridad</h3>
              <div className="create-contact-associate-deal">
                {stagesForNewDeal.length > 0 && (
                  <div className="create-contact-deal-row">
                    <label className="create-contact-label">Etapa</label>
                    <div style={{ marginBottom: 12 }}>
                      <SearchableSelect
                        value={selectedStageLabelNewDeal}
                        onChange={(value) => {
                          const s = stagesForNewDeal.find((x) => x.label === value);
                          setNewDealStageId(s ? s.id : '');
                        }}
                        options={stageOptionsNewDeal}
                        placeholder="Seleccionar etapa..."
                        allowCustomInput={false}
                        allowSearch={false}
                        prefix={<span style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: selectedStageColorNewDeal, flexShrink: 0 }} aria-hidden />}
                        getOptionColor={getStageOptionColor}
                      />
                    </div>
                  </div>
                )}
                <div className="create-contact-deal-row">
                  <label className="create-contact-label">Prioridad</label>
                  <div style={{ marginBottom: 12 }}>
                    <SearchableSelect
                      value={priorityLabelNewDeal}
                      onChange={(value) => setNewDealPriority(value === 'Alta' ? 'high' : value === 'Baja' ? 'low' : 'medium')}
                      options={priorityOptionsNewDeal}
                      placeholder="Seleccionar prioridad..."
                      allowCustomInput={false}
                      allowSearch={false}
                      prefix={<span style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: getPriorityColor(priorityLabelNewDeal), flexShrink: 0 }} aria-hidden />}
                      getOptionColor={getPriorityColor}
                    />
                  </div>
                </div>
              </div>
            </section>
          );

          if (isScesde26Pipeline) {
            return (
              <>
                <section className="create-contact-section">
                  <p className="create-contact-hint" style={{ marginBottom: 12 }}>
                    Pipeline seleccionado: <strong>{pipelineNameDeal}</strong>. Complete los datos para crear el negocio.
                  </p>
                </section>
                {etapaPrioridadSection}
                <section className="create-contact-section">
                  <h3>Modalidad de participación</h3>
                  <ParticipationModalitySection
                    selectedModalities={participationModalities}
                    onChange={setParticipationModalities}
                    agreedModality={agreedModality}
                    modalityObservations={modalityObservations}
                    eventProgramParticipation={eventProgramParticipation as import('../types/profile.types').EventProgramParticipation[]}
                    onEventProgramParticipationChange={setEventProgramParticipation}
                    requestInformation={requestInformation}
                    onRequestInformationChange={setRequestInformation}
                    createContactMode
                  />
                </section>

                <section className="create-contact-section create-contact-stands-wrap">
                  <h3>Stands de interés</h3>
                  <ExpoZoneSection
                    selectedStands={selectedStands}
                    confirmedStands={confirmedStands}
                    onChange={setSelectedStands}
                    onConfirmedChange={setConfirmedStands}
                    requestInformation={requestExpoInformation}
                    onRequestInformationChange={setRequestExpoInformation}
                    createContactMode
                  />
                </section>
              </>
            );
          }
          const selectedPipeline = pipelinesList.find((p) => p.id === selectedPipelineId);
          const otherPipelineName = selectedPipeline?.name ?? 'este pipeline';
          const otherFieldSchema = selectedPipeline?.field_schema ?? [];
          if (otherFieldSchema.length === 0) {
            return (
              <>
                <section className="create-contact-section">
                  <p className="create-contact-hint" style={{ marginBottom: 8 }}>
                    Pipeline seleccionado: <strong>{otherPipelineName}</strong>. Complete los datos para crear el negocio.
                  </p>
                  <p className="create-contact-hint">
                    El negocio se creará en el pipeline <strong>{otherPipelineName}</strong>. Este pipeline no tiene campos personalizados definidos.
                  </p>
                </section>
                {etapaPrioridadSection}
              </>
            );
          }
          return (
            <>
              <section className="create-contact-section">
                <p className="create-contact-hint" style={{ marginBottom: 12 }}>
                  Pipeline seleccionado: <strong>{otherPipelineName}</strong>. Complete los datos para crear el negocio.
                </p>
              </section>
              {etapaPrioridadSection}
              <section className="create-contact-section">
              <div className="create-contact-associate-deal">
                {otherFieldSchema.map((field) => (
                  <div key={field.key} className="create-contact-deal-row">
                    <label className="create-contact-label" htmlFor={`create-deal-${field.key}`}>
                      {(field.label || '').replace(/\s*\(actualizado\)\s*/gi, '').trim() || field.key}
                    </label>
                    <input
                      id={`create-deal-${field.key}`}
                      type="text"
                      className="create-contact-input"
                      value={otherPipelineDealData[field.key] ?? ''}
                      onChange={(e) => setOtherPipelineDealData((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      placeholder={(field.label || '').replace(/\s*\(actualizado\)\s*/gi, '').trim() || field.key}
                    />
                  </div>
                ))}
              </div>
            </section>
            </>
          );
        })()}
      </div>
      {asSidebar && (
        <div className="create-contact-profile-checkbox-block">
          <label className="checkbox-item create-contact-associate-checkbox">
            <input
              type="checkbox"
              checked={createProfileForContact}
              onChange={(e) => setCreateProfileForContact(e.target.checked)}
            />
            <span className="checkbox-label">Crear perfil de usuario para este contacto (correo del contacto + contraseña generada)</span>
          </label>
        </div>
      )}
      <div className="create-contact-modal-footer">
        <button type="button" className="create-contact-footer-btn secondary" onClick={handleClose}>
          Cancelar
        </button>
        <button
          type="button"
          className="create-contact-footer-btn primary"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Creando…' : 'Crear contacto'}
        </button>
      </div>
    </>
  );

  if (asSidebar) {
    return (
      <div className={`create-contact-sidebar-inner ${isDarkMode ? 'dark' : ''}`}>
        {formContent}
      </div>
    );
  }

  return (
    <div className="create-contact-modal-overlay" onClick={handleClose}>
      <div
        className={`create-contact-modal ${isDarkMode ? 'dark' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {formContent}
      </div>
    </div>
  );
};

export default CreateContactModal;
